import type { RadialHover } from './renderRadial';
import type { RadialPlot, RadialSeries } from './types';

const NAVIGATION_KEYS = [
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Escape',
] as const;

export type RadialNavigationKey = (typeof NAVIGATION_KEYS)[number];

export const isRadialNavigationKey = (
  key: string
): key is RadialNavigationKey =>
  NAVIGATION_KEYS.some(navigationKey => navigationKey === key);

const toValuedIndices = ({ points }: RadialSeries): number[] =>
  points.flatMap(({ value }, index) => (value === null ? [] : [index]));

const wrap = (index: number, length: number): number =>
  ((index % length) + length) % length;

const toNearest = (candidates: number[], target: number): number | null =>
  candidates.reduce<number | null>(
    (nearest, candidate) =>
      nearest === null ||
      Math.abs(candidate - target) < Math.abs(nearest - target)
        ? candidate
        : nearest,
    null
  );

const toFirstHover = (plot: RadialPlot): RadialHover | null => {
  const seriesIndex = plot.series.findIndex(
    series => toValuedIndices(series).length > 0
  );
  if (seriesIndex === -1) return null;
  return {
    seriesIndex,
    pointIndex: toValuedIndices(plot.series[seriesIndex])[0],
  };
};

const stepPoint = (
  plot: RadialPlot,
  { seriesIndex, pointIndex }: RadialHover,
  step: number
): RadialHover => {
  const valued = toValuedIndices(plot.series[seriesIndex]);
  const position = valued.indexOf(pointIndex);
  return {
    seriesIndex,
    pointIndex: valued[wrap(position + step, valued.length)],
  };
};

const stepSeries = (
  plot: RadialPlot,
  { seriesIndex, pointIndex }: RadialHover,
  step: number
): RadialHover => {
  const navigable = plot.series.flatMap((series, index) =>
    toValuedIndices(series).length > 0 ? [index] : []
  );
  const nextSeriesIndex =
    navigable[wrap(navigable.indexOf(seriesIndex) + step, navigable.length)];
  const nearest = toNearest(
    toValuedIndices(plot.series[nextSeriesIndex]),
    pointIndex
  );
  if (nearest === null) return { seriesIndex, pointIndex };
  return { seriesIndex: nextSeriesIndex, pointIndex: nearest };
};

const isOnPlot = (plot: RadialPlot, hover: RadialHover): boolean => {
  const value = plot.series[hover.seriesIndex]?.points[hover.pointIndex]?.value;
  return value !== null && value !== undefined;
};

/**
 * The vertex a navigation key moves to. Left and right step through a series' values, wrapping;
 * up and down step between series, keeping the nearest spoke. The first arrow from nowhere lands
 * on the first value; Escape clears.
 */
export const toKeyboardHover = (
  plot: RadialPlot,
  current: RadialHover | null,
  key: RadialNavigationKey
): RadialHover | null => {
  if (key === 'Escape') return null;
  if (!current || !isOnPlot(plot, current)) return toFirstHover(plot);

  switch (key) {
    case 'ArrowRight':
      return stepPoint(plot, current, 1);
    case 'ArrowLeft':
      return stepPoint(plot, current, -1);
    case 'ArrowDown':
      return stepSeries(plot, current, 1);
    case 'ArrowUp':
      return stepSeries(plot, current, -1);
  }
};
